import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MainLayout from '../../layouts/MainLayout';
import {
  Button,
  Card,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Progress,
  Select,
  Slider,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { callCenterQueuesApi, callCenterStandardsApi, show422Error, ValidationError } from '../../utils/api';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import PermissionGate from '../../components/PermissionGate';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';

const unwrap = (res) => {
  const payload = res?.data ?? [];
  return Array.isArray(payload) ? payload : payload.data ?? [];
};

const queueLabel = (queue) => queue?.title || queue?.name || '—';

const CallCenterStandards = () => {
  const [standards, setStandards] = useState([]);
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [queueFilter, setQueueFilter] = useState(undefined);
  const [modal, setModal] = useState({ open: false, editing: null });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [standardsRes, queuesRes] = await Promise.all([callCenterStandardsApi.getAll(), callCenterQueuesApi.getAll()]);
      setStandards(unwrap(standardsRes));
      setQueues(unwrap(queuesRes));
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت استانداردها');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // مجموع وزن استانداردهای فعال هر صف؛ سهم هر استاندارد = وزن / مجموع
  const totals = useMemo(() => {
    const map = {};
    standards.filter((s) => s.is_active).forEach((s) => {
      map[s.queue_id] = (map[s.queue_id] || 0) + s.weight;
    });
    return map;
  }, [standards]);

  const rows = useMemo(
    () => (queueFilter ? standards.filter((s) => s.queue_id === queueFilter) : standards),
    [standards, queueFilter],
  );

  const openModal = (editing = null) => {
    setModal({ open: true, editing });
    form.setFieldsValue(editing
      ? {
        queue_id: editing.queue_id,
        name: editing.name,
        description: editing.description || '',
        weight: editing.weight,
        is_active: editing.is_active,
      }
      : { queue_id: queueFilter, name: '', description: '', weight: 10, is_active: true });
  };

  const closeModal = () => {
    setModal({ open: false, editing: null });
    form.resetFields();
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        queue_id: values.queue_id,
        name: values.name.trim(),
        description: values.description?.trim() || null,
        weight: values.weight,
        is_active: !!values.is_active,
      };
      if (modal.editing) {
        await callCenterStandardsApi.update(modal.editing.id, payload);
        message.success('استاندارد ویرایش شد');
      } else {
        await callCenterStandardsApi.create(payload);
        message.success('استاندارد جدید اضافه شد');
      }
      closeModal();
      await load();
    } catch (error) {
      if (error?.errorFields) return;
      if (error instanceof ValidationError) {
        show422Error(error, form);
      } else {
        message.error(error?.message || 'خطا در ذخیره استاندارد');
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (record, checked) => {
    setTogglingId(record.id);
    try {
      await callCenterStandardsApi.update(record.id, {
        queue_id: record.queue_id,
        name: record.name,
        description: record.description,
        weight: record.weight,
        is_active: checked,
      });
      await load();
    } catch (error) {
      message.error(error?.message || 'خطا در تغییر وضعیت');
    } finally {
      setTogglingId(null);
    }
  };

  const remove = async (record) => {
    setDeletingId(record.id);
    try {
      await callCenterStandardsApi.delete(record.id);
      message.success('استاندارد حذف شد');
      await load();
    } catch (error) {
      message.error(error?.message || 'خطا در حذف استاندارد');
    } finally {
      setDeletingId(null);
    }
  };

  const columns = [
    {
      title: 'صف',
      key: 'queue',
      width: 150,
      render: (_, r) => <Tag className="custom-font m-0" color="blue">{queueLabel(r.queue)}</Tag>,
    },
    {
      title: 'استاندارد',
      key: 'name',
      render: (_, r) => (
        <div className="min-w-0">
          <div className="custom-font font-medium text-gray-800 dark:text-gray-100">{r.name}</div>
          {r.description && <div className="custom-font text-xs text-gray-500 dark:text-gray-400 leading-5">{r.description}</div>}
        </div>
      ),
    },
    {
      title: 'وزن',
      key: 'weight',
      width: 220,
      render: (_, r) => {
        const total = totals[r.queue_id] || 0;
        const share = r.is_active && total > 0 ? Math.round((r.weight / total) * 1000) / 10 : 0;
        return (
          <Tooltip title={r.is_active ? `سهم از امتیاز نهایی این صف: ${share}٪` : 'غیرفعال؛ در امتیازدهی لحاظ نمی‌شود'}>
            <div className="flex items-center gap-2">
              <Progress percent={share} showInfo={false} size="small" className="flex-1 m-0" strokeColor="#3b82f6" />
              <span className="custom-font text-xs text-gray-500 w-20 text-left" dir="ltr">
                {r.weight} · {share}%
              </span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'فعال',
      key: 'is_active',
      width: 80,
      render: (_, r) => (
        <PermissionGate module="callcenter" permission="manage_callcenter_standards">
          <Switch size="small" checked={r.is_active} loading={togglingId === r.id} onChange={(c) => toggleActive(r, c)} />
        </PermissionGate>
      ),
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 110,
      render: (_, r) => (
        <PermissionGate module="callcenter" permission="manage_callcenter_standards">
          <Space size={2}>
            <Tooltip title="ویرایش">
              <Button type="text" icon={<EditOutlined />} onClick={() => openModal(r)} />
            </Tooltip>
            <Popconfirm
              title={<span className="custom-font">حذف استاندارد «{r.name}»؟</span>}
              description={<span className="custom-font text-xs">تماس‌های قبلی تغییر نمی‌کنند؛ فقط تحلیل‌های بعدی بدون این استاندارد انجام می‌شود.</span>}
              onConfirm={() => remove(r)}
              okText="حذف"
              cancelText="انصراف"
              okButtonProps={{ danger: true }}
              placement="topRight"
            >
              <Button type="text" danger icon={<DeleteOutlined />} loading={deletingId === r.id} />
            </Popconfirm>
          </Space>
        </PermissionGate>
      ),
    },
  ];

  const queueOptions = queues.map((q) => ({ value: q.id, label: queueLabel(q) }));

  return (
    <MainLayout
      title="استانداردهای ارزیابی"
      activeMenu="/call-center/standards"
      breadcrumbs={[
        { title: 'مرکز تماس', path: '/call-center/calls' },
        { title: 'استانداردهای ارزیابی' },
      ]}
    >
      <div className="w-full custom-font">
        <PermissionGate module="callcenter" permission="manage_callcenter_standards" showWarning>
          <Card
            bordered={false}
            className="shadow-sm rounded-lg"
            styles={{ body: { padding: '1.5rem' } }}
            title={(
              <div className="flex items-center gap-2">
                <SafetyCertificateOutlined className="text-gray-500" />
                <span className="font-semibold text-gray-800 dark:text-gray-100">استانداردهای ارزیابی اپراتور</span>
                <span className="text-xs text-gray-400 font-normal hidden md:inline">
                  برای هر صف معیارهای وزن‌دار تعریف کنید؛ هوش مصنوعی بر همین اساس امتیاز اپراتور را می‌دهد
                </span>
              </div>
            )}
            extra={(
              <Space wrap>
                <Select
                  allowClear
                  placeholder="همه صف‌ها"
                  value={queueFilter}
                  onChange={setQueueFilter}
                  options={queueOptions}
                  className="w-44"
                />
                <Button icon={<ReloadOutlined />} onClick={load}>به‌روزرسانی</Button>
                <Button type="primary" className={PANEL_PRIMARY_BTN_CLASS} icon={<PlusOutlined />} onClick={() => openModal()}>
                  افزودن استاندارد
                </Button>
              </Space>
            )}
          >
            {loading && standards.length === 0 ? (
              <TableSkeleton columns={5} rows={5} />
            ) : (
              <Table
                columns={columns}
                dataSource={rows}
                rowKey="id"
                pagination={rows.length > 20 ? { pageSize: 20, showSizeChanger: false } : false}
                size="middle"
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={<span className="custom-font text-gray-500">برای این صف هنوز استانداردی تعریف نشده است؛ تا آن زمان امتیاز با معیارهای پیش‌فرض داده می‌شود.</span>}
                    />
                  ),
                }}
              />
            )}
          </Card>
        </PermissionGate>
      </div>

      <Modal
        title={<span className="custom-font">{modal.editing ? 'ویرایش استاندارد' : 'افزودن استاندارد'}</span>}
        open={modal.open}
        onCancel={closeModal}
        onOk={submit}
        confirmLoading={saving}
        okText="ذخیره"
        cancelText="انصراف"
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" className="mt-4 custom-font" onFinish={submit}>
          <Form.Item name="queue_id" label="صف" rules={[{ required: true, message: 'صف را انتخاب کنید' }]}>
            <Select placeholder="مثال: فروش" options={queueOptions} showSearch optionFilterProp="label" />
          </Form.Item>

          <Form.Item
            name="name"
            label="نام استاندارد"
            rules={[
              { required: true, whitespace: true, message: 'نام استاندارد الزامی است' },
              { max: 100, message: 'حداکثر ۱۰۰ کاراکتر' },
            ]}
          >
            <Input placeholder="مثال: نیازسنجی، معرفی محصول، تعیین اقدام بعدی" maxLength={100} autoFocus />
          </Form.Item>

          <Form.Item
            name="description"
            label="معیار سنجش (اختیاری اما پیشنهاد می‌شود)"
            extra="توضیح دهید AI چه چیزی را بررسی کند؛ مثلاً «اپراتور حداقل دو پرسش درباره نیاز و بودجه بپرسد»."
            rules={[{ max: 1000, message: 'حداکثر ۱۰۰۰ کاراکتر' }]}
          >
            <Input.TextArea rows={3} maxLength={1000} showCount />
          </Form.Item>

          <Form.Item label="وزن (۱ تا ۱۰۰)" required>
            <div className="flex items-center gap-3">
              <Form.Item name="weight" noStyle rules={[{ required: true, message: 'وزن را وارد کنید' }]}>
                <Slider min={1} max={100} className="flex-1" />
              </Form.Item>
              <Form.Item name="weight" noStyle>
                <InputNumber min={1} max={100} precision={0} />
              </Form.Item>
            </div>
            <div className="text-xs text-gray-400">وزن‌ها نسبی‌اند؛ سهم هر استاندارد = وزن آن ÷ مجموع وزن استانداردهای فعال همان صف.</div>
          </Form.Item>

          <Form.Item name="is_active" label="فعال" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default CallCenterStandards;
